/**
 * Per-inventory customizable sectors: overlay an inventory's own sector tree
 * onto its `methodology.sectorList` so every downstream consumer reads the
 * right tree without each one having to choose between the two paths.
 *
 * Detection: a materialized custom inventory has `inventory.sectorList[0]`
 * carrying `inventoryId === inventory.id`. Standard inventories' sectorList
 * (when fetched) mirrors the methodology tree anyway, so the swap is a no-op
 * for them.
 *
 * Why this lives outside any single hook: useGetInventory uses it for the
 * detail page, useGetInventoryList for the table, and useInventoryComparison
 * for the /inventories/all cross-inventory chart (the last one runs its own
 * useQuery directly and would forget the overlay otherwise). Pulling it here
 * keeps the rule in one place.
 */

interface InventoryWithMethodology<M extends object, S extends { inventoryId?: string | null }> {
  id: string;
  sectorList?: S[] | null;
  methodology?: M | null;
}

export function overlayInventorySectorList<
  M extends object,
  S extends { inventoryId?: string | null },
  T extends InventoryWithMethodology<M, S>,
>(inventory: T): T {
  const owns =
    Array.isArray(inventory.sectorList) &&
    inventory.sectorList.length > 0 &&
    inventory.sectorList[0]?.inventoryId === inventory.id;

  if (!owns || !inventory.methodology) return inventory;

  return {
    ...inventory,
    methodology: { ...inventory.methodology, sectorList: inventory.sectorList },
  };
}
